import type { Dict, Lang } from "@/i18n/translations";

export type HomepageSection =
  | "hero"
  | "why"
  | "gallery"
  | "practical"
  | "travel"
  | "faq"
  | "footer";
export const HOMEPAGE_SECTIONS: HomepageSection[] = [
  "hero",
  "why",
  "gallery",
  "practical",
  "travel",
  "faq",
  "footer",
];

export type HomepageOverrides = Partial<{
  hero: Partial<Dict["hero"]>;
  why: Partial<Dict["why"]>;
  gallery: Partial<Dict["gallery"]>;
  practical: Partial<Dict["practical"]>;
  travel: Partial<Dict["travel"]>;
  faq: Partial<Dict["faq"]>;
  footer: Partial<Dict["footer"]>;
}>;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Deep-merge overrides into defaults. Arrays are replaced when the override
 *  supplies one; nested objects merge recursively; null/undefined leaves fall
 *  back to the default. */
export function deepMerge<T>(defaults: T, override: unknown): T {
  if (override === undefined || override === null) return defaults;
  if (Array.isArray(defaults)) {
    if (!Array.isArray(override)) {
      // Overrides saved via dot-paths (e.g. "reasons.2.body") arrive as an
      // object keyed by numeric index. Merge those per index.
      if (isPlainObject(override)) {
        return defaults.map((d, i) =>
          i in override || String(i) in override
            ? deepMerge(d, (override as Record<string, unknown>)[String(i)])
            : d,
        ) as unknown as T;
      }
      return defaults;
    }
    // Merge element-wise so partially-filled arrays still fall back per-item.
    return defaults.map((d, i) =>
      i < override.length ? deepMerge(d, override[i]) : d,
    ) as unknown as T;
  }
  if (isPlainObject(defaults) && isPlainObject(override)) {
    const out: Record<string, unknown> = { ...defaults };
    for (const k of Object.keys(override)) {
      out[k] = deepMerge((defaults as Record<string, unknown>)[k], override[k]);
    }
    return out as T;
  }
  // Primitive: only override with non-empty strings; empty strings mean "reset".
  if (typeof override === "string" && override.length === 0) return defaults;
  return override as T;
}

export function mergeDict(defaults: Dict, overrides: HomepageOverrides | null): Dict {
  if (!overrides) return defaults;
  return {
    ...defaults,
    hero: deepMerge(defaults.hero, overrides.hero),
    why: deepMerge(defaults.why, overrides.why),
    gallery: deepMerge(defaults.gallery, overrides.gallery),
    practical: deepMerge(defaults.practical, overrides.practical),
    travel: deepMerge(defaults.travel, overrides.travel),
    faq: deepMerge(defaults.faq, overrides.faq),
    footer: deepMerge(defaults.footer, overrides.footer),
  };
}

// ---------- Admin schema ----------

export type FieldKind = "input" | "textarea" | "rich";
export type FieldDef =
  | { kind: FieldKind; path: string; label: string; help?: string }
  | {
      kind: "list";
      path: string; // path to the array (e.g. "stats", "cards", "items")
      label: string;
      itemLabel: (index: number, item: Record<string, unknown>) => string;
      fields: FieldDef[]; // relative to each item
    };

export const SECTION_LABELS: Record<HomepageSection, string> = {
  hero: "Hero (bovenkant)",
  why: "Waarom",
  gallery: "Sfeer (foto's)",
  practical: "Praktisch",
  travel: "Bereikbaarheid",
  faq: "FAQ",
  footer: "Footer",
};

export const SECTION_SCHEMAS: Record<HomepageSection, FieldDef[]> = {
  hero: [
    { kind: "input", path: "badge", label: "Badge (bovenaan)" },
    { kind: "input", path: "title1", label: "Titel — regel 1" },
    { kind: "input", path: "title2", label: "Titel — regel 2 (cursief goud)" },
    { kind: "input", path: "title3", label: "Titel — regel 3" },
    { kind: "rich", path: "intro", label: "Introtekst" },
    { kind: "input", path: "dates", label: "Label: datums" },
    { kind: "input", path: "datesValue", label: "Datums-waarde" },
    { kind: "input", path: "location", label: "Label: locatie" },
    { kind: "input", path: "locationValue", label: "Locatie-waarde" },
    { kind: "input", path: "capacity", label: "Label: capaciteit" },
    { kind: "input", path: "capacityOutside", label: "Capaciteit buiten" },
    { kind: "input", path: "capacityCovered", label: "Capaciteit overdekt" },
    { kind: "input", path: "note", label: "Notitie onder knoppen" },
    { kind: "input", path: "tickerNow", label: "Ticker links" },
    { kind: "input", path: "tickerTime", label: "Ticker midden" },
    { kind: "input", path: "tickerParking", label: "Ticker rechts" },
    { kind: "input", path: "heroAlt", label: "Alt-tekst hero-afbeelding" },
  ],
  why: [
    { kind: "input", path: "eyebrow", label: "Eyebrow" },
    { kind: "input", path: "title", label: "Titel" },
    { kind: "rich", path: "body", label: "Body" },
    {
      kind: "list",
      path: "stats",
      label: "Statistieken (vast 4)",
      itemLabel: (i, it) => `Statistiek ${i + 1} — ${String(it.value ?? "")}`,
      fields: [
        { kind: "input", path: "value", label: "Waarde" },
        { kind: "input", path: "suffix", label: "Suffix" },
        { kind: "input", path: "caption", label: "Bijschrift" },
      ],
    },
    {
      kind: "list",
      path: "reasons",
      label: "Redenen (vast 4)",
      itemLabel: (i, it) => `Reden ${i + 1} — ${String(it.title ?? "")}`,
      fields: [
        { kind: "input", path: "title", label: "Titel" },
        { kind: "textarea", path: "body", label: "Tekst" },
      ],
    },
  ],
  gallery: [
    { kind: "input", path: "eyebrow", label: "Eyebrow" },
    { kind: "input", path: "title1", label: "Titel — regel 1" },
    { kind: "input", path: "title2", label: "Titel — regel 2 (cursief goud)" },
    { kind: "textarea", path: "body", label: "Introtekst" },
    { kind: "input", path: "captions.screen", label: "Bijschrift — grote foto (doek)" },
    { kind: "input", path: "captions.audience", label: "Bijschrift — publiek" },
    { kind: "input", path: "captions.horeca", label: "Bijschrift — horeca" },
    { kind: "input", path: "captions.couple", label: "Bijschrift — koppel" },
    { kind: "input", path: "captions.drinks", label: "Bijschrift — drankjes" },
    { kind: "input", path: "alts.screen", label: "Alt-tekst — grote foto (doek)" },
    { kind: "input", path: "alts.audience", label: "Alt-tekst — publiek" },
    { kind: "input", path: "alts.horeca", label: "Alt-tekst — horeca" },
    { kind: "input", path: "alts.couple", label: "Alt-tekst — koppel" },
    { kind: "input", path: "alts.drinks", label: "Alt-tekst — drankjes" },
  ],
  practical: [
    { kind: "input", path: "eyebrow", label: "Eyebrow" },
    { kind: "input", path: "title1", label: "Titel — regel 1" },
    { kind: "input", path: "title2", label: "Titel — regel 2 (cursief goud)" },
    {
      kind: "list",
      path: "cards",
      label: "Kaartjes",
      itemLabel: (i, it) => `Kaart ${i + 1} — ${String(it.title ?? "")}`,
      fields: [
        { kind: "input", path: "title", label: "Titel" },
        // lines is a string[]; we render it as a textarea (regels via newlines).
        { kind: "textarea", path: "lines", label: "Regels (één per regel)" },
      ],
    },
  ],
  travel: [
    { kind: "input", path: "eyebrow", label: "Eyebrow" },
    { kind: "input", path: "title1", label: "Titel — regel 1" },
    { kind: "input", path: "title2", label: "Titel — regel 2 (cursief goud)" },
    { kind: "textarea", path: "body", label: "Introtekst" },
    { kind: "input", path: "mapLabel", label: "Label boven kaartje" },
    { kind: "input", path: "address", label: "Adres" },
    {
      kind: "input",
      path: "mapsUrl",
      label: "Google Maps-link (locatie)",
      help: "Volledige https-link naar Google Maps",
    },
    {
      kind: "list",
      path: "modes",
      label: "Vervoersopties (vast 4: fiets, auto, OV, lopen)",
      itemLabel: (i, it) => `Optie ${i + 1} — ${String(it.title ?? "")}`,
      fields: [
        { kind: "input", path: "title", label: "Titel" },
        { kind: "input", path: "tag", label: "Label rechtsboven" },
        { kind: "textarea", path: "body", label: "Tekst" },
      ],
    },
    { kind: "input", path: "freeParking.badge", label: "Gratis parkeren — badge" },
    { kind: "input", path: "freeParking.title", label: "Gratis parkeren — titel" },
    { kind: "textarea", path: "freeParking.body", label: "Gratis parkeren — tekst" },
    { kind: "textarea", path: "freeParking.walk", label: "Gratis parkeren — wandeling" },
    { kind: "textarea", path: "freeParking.note", label: "Gratis parkeren — let op" },
    { kind: "input", path: "freeParking.cta", label: "Gratis parkeren — knoptekst" },
    {
      kind: "input",
      path: "freeParking.mapsUrl",
      label: "Gratis parkeren — Google Maps-link",
      help: "Volledige https-link, bv. https://www.google.com/maps/search/?api=1&query=52.206375,5.919837",
    },
    { kind: "textarea", path: "freeParking.disclaimer", label: "Gratis parkeren — disclaimer" },
  ],
  faq: [
    { kind: "input", path: "eyebrow", label: "Eyebrow" },
    { kind: "input", path: "title1", label: "Titel — regel 1" },
    { kind: "input", path: "title2", label: "Titel — regel 2 (cursief goud)" },
    { kind: "textarea", path: "intro", label: "Intro" },
    {
      kind: "list",
      path: "items",
      label: "Vragen",
      itemLabel: (i, it) => `V${i + 1} — ${String(it.q ?? "")}`,
      fields: [
        { kind: "input", path: "q", label: "Vraag" },
        { kind: "rich", path: "a", label: "Antwoord" },
      ],
    },
  ],
  footer: [
    { kind: "textarea", path: "tagline", label: "Tagline (onder logo)" },
    { kind: "input", path: "edition", label: "Editie-tekst" },
    { kind: "input", path: "location", label: "Label: locatie" },
    { kind: "input", path: "contact", label: "Label: contact" },
    {
      kind: "input",
      path: "copyright",
      label: "Copyright-regel",
      help: "Gebruik {year} voor het huidige jaartal, bv. © {year} Openluchtfilmfestival Apeldoorn",
    },
    {
      kind: "textarea",
      path: "rights",
      label: "Rechten-regel (HTML toegestaan)",
      help: 'HTML-links zijn toegestaan, bv. <a href="…">Delicious Media</a>',
    },
    { kind: "input", path: "privacy", label: "Label: privacy" },
    { kind: "input", path: "terms", label: "Label: voorwaarden" },
    { kind: "input", path: "ticketPartner", label: "Label: ticketpartner" },
  ],
};

// ---------- get/set by dot path ----------

export function getPath(obj: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc === undefined || acc === null) return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

export function setPath<T extends Record<string, unknown>>(obj: T, path: string, value: unknown): T {
  const keys = path.split(".");
  const clone = (v: unknown): Record<string, unknown> | unknown[] =>
    Array.isArray(v) ? [...v] : isPlainObject(v) ? { ...v } : {};
  const next = clone(obj) as Record<string, unknown>;
  let cur: Record<string, unknown> = next;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = clone(cur[k]);
    cur = cur[k] as Record<string, unknown>;
  }
  cur[keys[keys.length - 1]] = value;
  return next as T;
}

export const LANG_LABELS: Record<Lang, string> = { nl: "Nederlands", en: "English", de: "Deutsch" };